import { TFunction } from 'i18next';
import { DocumentOutputFolderRule, DocumentOutputPurpose } from 'models/documentOutputLocation';
import { TaskTypes } from 'models/tasks/task';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';

// Translation keys cannot be built from variables, so every purpose, rule and status code has its own case.

export function getOutputPurposeTitle(purpose: DocumentOutputPurpose, t: TFunction<string[]>): string {
  switch (purpose) {
    case DocumentOutputPurpose.GeneralReports:
      return t('outputLocations:Purpose.GeneralReports.Title');
    case DocumentOutputPurpose.AuditReports:
      return t('outputLocations:Purpose.AuditReports.Title');
    case DocumentOutputPurpose.EvidenceUploads:
      return t('outputLocations:Purpose.EvidenceUploads.Title');
    case DocumentOutputPurpose.ReferenceEvidence:
      return t('outputLocations:Purpose.ReferenceEvidence.Title');
    case DocumentOutputPurpose.EmailAttachments:
      return t('outputLocations:Purpose.EmailAttachments.Title');
    case DocumentOutputPurpose.KpiAttachments:
      return t('outputLocations:Purpose.KpiAttachments.Title');
    case DocumentOutputPurpose.AssetDocuments:
      return t('outputLocations:Purpose.AssetDocuments.Title');
    default:
      return '';
  }
}

export function getOutputPurposeInfo(purpose: DocumentOutputPurpose, t: TFunction<string[]>): string {
  switch (purpose) {
    case DocumentOutputPurpose.GeneralReports:
      return t('outputLocations:Purpose.GeneralReports.Info');
    case DocumentOutputPurpose.AuditReports:
      return t('outputLocations:Purpose.AuditReports.Info');
    case DocumentOutputPurpose.EvidenceUploads:
      return t('outputLocations:Purpose.EvidenceUploads.Info');
    case DocumentOutputPurpose.ReferenceEvidence:
      return t('outputLocations:Purpose.ReferenceEvidence.Info');
    case DocumentOutputPurpose.EmailAttachments:
      return t('outputLocations:Purpose.EmailAttachments.Info');
    case DocumentOutputPurpose.KpiAttachments:
      return t('outputLocations:Purpose.KpiAttachments.Info');
    case DocumentOutputPurpose.AssetDocuments:
      return t('outputLocations:Purpose.AssetDocuments.Info');
    default:
      return '';
  }
}

export function getOutputFolderRuleText(rule: DocumentOutputFolderRule, t: TFunction<string[]>): string {
  switch (rule) {
    case DocumentOutputFolderRule.PerReportDefinition:
      return t('outputLocations:FolderRule.PerReportDefinition');
    case DocumentOutputFolderRule.PerAudit:
      return t('outputLocations:FolderRule.PerAudit');
    case DocumentOutputFolderRule.PerTask:
      return t('outputLocations:FolderRule.PerTask');
    case DocumentOutputFolderRule.PerKpi:
      return t('outputLocations:FolderRule.PerKpi');
    case DocumentOutputFolderRule.PerAsset:
      return t('outputLocations:FolderRule.PerAsset');
    default:
      return t('outputLocations:FolderRule.Flat');
  }
}

/** The chip label of a token of the folder name format; undefined for a token the web does not know, which is left out. */
export function getFolderNameTokenLabel(key: string, t: TFunction<string[]>): string | undefined {
  switch (key) {
    case 'TaskId':
      return t('outputLocations:Evidence.Token.TaskId');
    case 'TaskName':
      return t('outputLocations:Evidence.Token.TaskName');
    case 'TemplateName':
      return t('outputLocations:Evidence.Token.TemplateName');
    case 'KpiId':
      return t('outputLocations:Evidence.Token.KpiId');
    case 'KpiName':
      return t('outputLocations:Evidence.Token.KpiName');
    case 'AssetId':
      return t('outputLocations:Evidence.Token.AssetId');
    case 'AssetName':
      return t('outputLocations:Evidence.Token.AssetName');
    case 'AssetCode':
      return t('outputLocations:Evidence.Token.AssetCode');
    case 'AssetType':
      return t('outputLocations:Evidence.Token.AssetType');
    case 'Date':
      return t('outputLocations:Evidence.Token.Date');
    default:
      return undefined;
  }
}

export function getTemplateTypeText(taskType: TaskTypes | undefined, t: TFunction<string[]>): string {
  return taskType === TaskTypes.EventTemplate
    ? t('outputLocations:Evidence.TemplateType.Event')
    : t('outputLocations:Evidence.TemplateType.Task');
}

/** A problem code of the folder name format, as the preview and a refused save report it. An unknown code shows as it is. */
export function getFolderNameProblemText(problem: string, t: TFunction<string[]>): string {
  switch (problem) {
    case 'Empty':
      return t('outputLocations:Evidence.Problem.Empty');
    case 'TooLong':
      return t('outputLocations:Evidence.Problem.TooLong');
    case 'TooManyLevels':
      return t('outputLocations:Evidence.Problem.TooManyLevels');
    case 'UnbalancedBrace':
      return t('outputLocations:Evidence.Problem.UnbalancedBrace');
    case 'UnknownToken':
      return t('outputLocations:Evidence.Problem.UnknownToken');
    case 'UnknownDateFormat':
      return t('outputLocations:Evidence.Problem.UnknownDateFormat');
    case 'EmptyLevel':
      return t('outputLocations:Evidence.Problem.EmptyLevel');
    case 'NoContent':
      return t('outputLocations:Evidence.Problem.NoContent');
    default:
      return problem;
  }
}

/**
 * The text of a refused save of evidence settings. A refused format (OUTPUTLOC0005) carries its problem codes in the
 * message, one per line, and they follow the general text. Undefined for an error that is no controller exception.
 */
/**
 * The start folder line of a template row: its own folder, else on the tenant's category the tenant's folder, else
 * the root of its own category.
 */
export function getTemplateFolderText(
  ownFolderName: string | undefined,
  hasOwnCategory: boolean,
  tenantFolderName: string | undefined,
  t: TFunction<string[]>,
): string {
  if (ownFolderName) return t('outputLocations:Folder.Line', { folder: ownFolderName });
  if (hasOwnCategory) return t('outputLocations:Folder.RootLine');

  return t('outputLocations:Evidence.FolderAsDefault', {
    folder: tenantFolderName ?? t('outputLocations:Folder.CategoryRoot'),
  });
}

export function getEvidenceSaveErrorText(error: unknown, t: TFunction<string[]>): string | undefined {
  const msg = AppError.getControllerExceptionMsg(error, t);
  if (
    msg === undefined ||
    AppError.getControllerExceptionCode(error) !== ControllerExceptionCodes.Code_OutputLocation_InvalidFormat
  ) {
    return msg;
  }

  const problems = ((error as AppError).data?.message ?? '')
    .split(/\r?\n/)
    .map((line: string) => line.trim())
    .filter((line: string) => line.length > 0)
    .map((line: string) => getFolderNameProblemText(line, t));

  return [msg, ...problems].join(' ');
}

/** True for the purposes that write reports: they have a template and a PDF choice. */
export function isReportPurpose(purpose: DocumentOutputPurpose): boolean {
  return purpose === DocumentOutputPurpose.GeneralReports || purpose === DocumentOutputPurpose.AuditReports;
}

const statusCodePrefix = 'OutputLocation.Status.';

/**
 * The status check reports its first failure as a code, and WriteNotProbed as a note when nothing failed; this is
 * the one place the codes become text. An unknown code gets the general text, so a new server code never shows
 * as a raw string.
 */
export function getOutputLocationStatusText(code: string | undefined, t: TFunction<string[]>): string | undefined {
  if (!code) return undefined;

  const name = code.startsWith(statusCodePrefix) ? code.substring(statusCodePrefix.length) : code;

  switch (name) {
    case 'IntegrationOff':
      return t('outputLocations:Status.IntegrationOff');
    case 'LocationNotSet':
      return t('outputLocations:Status.LocationNotSet');
    case 'LocationNotFound':
      return t('outputLocations:Status.LocationNotFound');
    case 'FolderNotFound':
      return t('outputLocations:Status.FolderNotFound');
    case 'ConnectionNotFound':
      return t('outputLocations:Status.ConnectionNotFound');
    case 'ConnectionDisabled':
      return t('outputLocations:Status.ConnectionDisabled');
    case 'ConnectionNotAuthorized':
      return t('outputLocations:Status.ConnectionNotAuthorized');
    case 'NotReachable':
      return t('outputLocations:Status.NotReachable');
    case 'NoWriteAccess':
      return t('outputLocations:Status.NoWriteAccess');
    case 'TemplateNotSet':
      return t('outputLocations:Status.TemplateNotSet');
    case 'TemplateMissing':
      return t('outputLocations:Status.TemplateMissing');
    case 'PdfNotAvailable':
      return t('outputLocations:Status.PdfNotAvailable');
    case 'WriteNotProbed':
      return t('outputLocations:Status.WriteNotProbed');
    default:
      return t('outputLocations:Status.Unknown');
  }
}
